// @ts-strict-ignore
import { useMutation, useQuery } from "@apollo/client";
import ActionDialog from "@dashboard/components/ActionDialog";
import { TopNav } from "@dashboard/components/AppLayout/TopNav";
import { DashboardCard } from "@dashboard/components/Card";
import CardMenu from "@dashboard/components/CardMenu";
import { ListPageLayout } from "@dashboard/components/Layouts";
import ResponsiveTable from "@dashboard/components/ResponsiveTable";
import TableCellHeader from "@dashboard/components/TableCellHeader";
import useNotifier from "@dashboard/hooks/useNotifier";
import {
  Chip,
  InputAdornment,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@material-ui/core";
import { makeStyles, SearchIcon } from "@saleor/macaw-ui";
import { Box, Button, Skeleton } from "@saleor/macaw-ui-next";
import React, { useState } from "react";

import { WalletOffer } from "../../types";
import { WALLET_OFFER_DELETE_MUTATION } from "../../walletMutations";
import { WALLET_OFFERS_QUERY } from "../../walletQueries";
import WalletOfferCreateDialog from "../WalletOfferCreateDialog/WalletOfferCreateDialog";

const useStyles = makeStyles(
  (theme: any) => ({
    searchCard: {
      padding: theme.spacing(2, 3),
      borderBottom: `1px solid ${theme.palette.divider}`,
    },
    codeCell: {
      fontWeight: 700,
      fontFamily: "monospace",
      fontSize: "0.95rem",
      color: theme.palette.primary.main,
    },
    badgeAll: {
      backgroundColor: "#dcfce7",
      color: "#15803d",
      fontWeight: 600,
      fontSize: "0.75rem",
    },
    badgeSingle: {
      backgroundColor: "#e0f2fe",
      color: "#0369a1",
      fontWeight: 600,
      fontSize: "0.75rem",
    },
    badgeMultiple: {
      backgroundColor: "#f3e8ff",
      color: "#7e22ce",
      fontWeight: 600,
      fontSize: "0.75rem",
    },
    emptyState: {
      padding: theme.spacing(6),
      textAlign: "center",
      color: theme.palette.text.secondary,
    },
  }),
  { name: "WalletOffersListPage" },
);

export const WalletOffersListPage: React.FC = () => {
  const classes = useStyles();
  const notify = useNotifier();

  const [search, setSearch] = useState("");
  const [openCreateDialog, setOpenCreateDialog] = useState(false);
  const [offerToEdit, setOfferToEdit] = useState<WalletOffer | null>(null);
  const [offerToDelete, setOfferToDelete] = useState<WalletOffer | null>(null);

  const { data, loading, refetch } = useQuery(WALLET_OFFERS_QUERY, {
    variables: { isStaffView: true },
    fetchPolicy: "cache-and-network",
  });

  const [deleteOffer, { loading: deleting }] = useMutation(WALLET_OFFER_DELETE_MUTATION, {
    refetchQueries: [{ query: WALLET_OFFERS_QUERY, variables: { isStaffView: true } }],
  });

  const offers: WalletOffer[] = data?.walletOffers || [];

  const filteredOffers = offers.filter(offer => {
    const q = search.toLowerCase().trim();

    if (!q) return true;

    const matchesOffer =
      offer.code.toLowerCase().includes(q) ||
      offer.title.toLowerCase().includes(q) ||
      offer.offerType.toLowerCase().includes(q);

    if (matchesOffer) return true;

    // Also match phone numbers or emails of targeted users
    if (offer.allowedUsers && offer.allowedUsers.length > 0) {
      return offer.allowedUsers.some(
        u =>
          (u.email && u.email.toLowerCase().includes(q)) ||
          (u.phoneNumber && u.phoneNumber.toLowerCase().includes(q)) ||
          (u.firstName && u.firstName.toLowerCase().includes(q)) ||
          (u.lastName && u.lastName.toLowerCase().includes(q)),
      );
    }

    return false;
  });

  const handleDeleteConfirm = async () => {
    if (!offerToDelete) return;

    try {
      const res = await deleteOffer({ variables: { id: offerToDelete.id } });

      if (res.errors && res.errors.length > 0) {
        notify({ status: "error", text: res.errors.map((e: any) => e.message).join(", ") });

        return;
      }

      if (!res.data?.walletOfferDelete) {
        notify({ status: "error", text: "Failed to delete offer: Action not permitted or request failed." });

        return;
      }

      const errors = res.data.walletOfferDelete.errors;

      if (errors && errors.length > 0) {
        notify({ status: "error", text: errors.map((e: any) => e.message).join(", ") });

        return;
      }

      notify({ status: "success", text: `Offer ${offerToDelete.code} deleted successfully.` });
      setOfferToDelete(null);
    } catch (err: any) {
      notify({ status: "error", text: err.message || "Failed to delete offer." });
    }
  };

  const renderTargetingBadge = (offer: WalletOffer) => {
    if (!offer.isTargeted || offer.allowedUsersCount === 0) {
      return <Chip size="small" label="All Users (Public)" className={classes.badgeAll} />;
    }

    if (offer.allowedUsersCount === 1) {
      const user = offer.allowedUsers?.[0];
      const identifier = user?.phoneNumber || user?.email || "1 User";

      return (
        <Chip
          size="small"
          label={`Specific User (${identifier})`}
          className={classes.badgeSingle}
          title={user?.email ? `${user.email} ${user.phoneNumber || ""}` : identifier}
        />
      );
    }

    return (
      <Chip
        size="small"
        label={`${offer.allowedUsersCount} Selected Users`}
        className={classes.badgeMultiple}
      />
    );
  };

  const renderBonusValue = (offer: WalletOffer) => {
    if (offer.offerType === "PERCENTAGE_CASHBACK") {
      return (
        <span>
          {offer.cashbackPercent}% Cashback
          {offer.maxBonusCap ? ` (up to ₹${offer.maxBonusCap})` : ""}
        </span>
      );
    }

    return <span>+₹{Number(offer.bonusAmount).toFixed(2)} Extra</span>;
  };

  return (
    <ListPageLayout>
      <TopNav title="Wallet Offers" isAlignToRight={false} withoutBorder>
        <Box __flex={1} display="flex" justifyContent="flex-end" alignItems="center">
          <Button
            variant="primary"
            onClick={() => {
              setOfferToEdit(null);
              setOpenCreateDialog(true);
            }}
            data-test-id="create-offer"
          >
            Create offer
          </Button>
        </Box>
      </TopNav>

      <DashboardCard>
        <Box className={classes.searchCard}>
          <TextField
            fullWidth
            variant="outlined"
            size="small"
            placeholder="Search wallet offers by code, title, or type..."
            value={search}
            onChange={e => setSearch(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon color="action" />
                </InputAdornment>
              ),
            }}
          />
        </Box>

        <ResponsiveTable>
          <TableHead>
            <TableRow>
              <TableCellHeader>Offer Code</TableCellHeader>
              <TableCellHeader>Title</TableCellHeader>
              <TableCellHeader>Offer Type</TableCellHeader>
              <TableCellHeader>Bonus / Cashback</TableCellHeader>
              <TableCellHeader>Min Top-Up</TableCellHeader>
              <TableCellHeader>Target Eligibility</TableCellHeader>
              <TableCellHeader>Valid Until</TableCellHeader>
              <TableCellHeader>Status</TableCellHeader>
              <TableCellHeader textAlign="right">Actions</TableCellHeader>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading && !offers.length ? (
              Array.from({ length: 4 }).map((_, idx) => (
                <TableRow key={idx}>
                  <TableCell colSpan={9}>
                    <Skeleton height={32} />
                  </TableCell>
                </TableRow>
              ))
            ) : filteredOffers.length === 0 ? (
              <TableRow>
                <TableCell colSpan={9} className={classes.emptyState}>
                  <Typography variant="body1">
                    No wallet offers found. Click &quot;Create offer&quot; to set up a new campaign.
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              filteredOffers.map(offer => {
                const isExpired = new Date(offer.validTo) < new Date();

                return (
                  <TableRow key={offer.id} hover>
                    <TableCell className={classes.codeCell}>{offer.code}</TableCell>
                    <TableCell>
                      <Typography variant="body2" style={{ fontWeight: 500 }}>
                        {offer.title}
                      </Typography>
                      {offer.description && (
                        <Typography variant="caption" color="textSecondary" display="block">
                          {offer.description}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell style={{ textTransform: "capitalize" }}>
                      {offer.offerType.replace("_", " ").toLowerCase()}
                    </TableCell>
                    <TableCell>{renderBonusValue(offer)}</TableCell>
                    <TableCell>₹{Number(offer.minTopupAmount).toFixed(2)}</TableCell>
                    <TableCell>{renderTargetingBadge(offer)}</TableCell>
                    <TableCell>
                      {new Date(offer.validTo).toLocaleDateString("en-IN", {
                        day: "2-digit",
                        month: "short",
                        year: "numeric",
                      })}
                    </TableCell>
                    <TableCell>
                      {isExpired ? (
                        <Chip size="small" label="Expired" variant="outlined" />
                      ) : (
                        <Chip size="small" label="Active" color="primary" />
                      )}
                    </TableCell>
                    <TableCell align="right">
                      <CardMenu
                        menuItems={[
                          {
                            label: "Edit Offer",
                            onSelect: () => {
                              setOfferToEdit(offer);
                              setOpenCreateDialog(true);
                            },
                          },
                          {
                            label: "Delete Offer",
                            onSelect: () => {
                              setOfferToDelete(offer);
                            },
                          },
                        ]}
                      />
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </ResponsiveTable>
      </DashboardCard>

      {/* Create / Edit Offer Dialog */}
      <WalletOfferCreateDialog
        open={openCreateDialog}
        onClose={() => {
          setOpenCreateDialog(false);
          setOfferToEdit(null);
        }}
        offerToEdit={offerToEdit}
        onSuccess={() => refetch()}
      />

      {/* Delete Confirmation Dialog */}
      <ActionDialog
        open={Boolean(offerToDelete)}
        onClose={() => setOfferToDelete(null)}
        onConfirm={handleDeleteConfirm}
        title="Delete Wallet Offer"
        confirmButtonState={deleting ? "loading" : "default"}
        variant="delete"
      >
        <Typography>
          Are you sure you want to permanently delete wallet offer{" "}
          <strong>{offerToDelete?.code}</strong>? Customers will no longer be able to claim this
          bonus.
        </Typography>
      </ActionDialog>
    </ListPageLayout>
  );
};

export default WalletOffersListPage;
